import React, { useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, Stack, Divider,
} from '@mui/material';
import {
  Keyboard as KeyboardIcon,
  Save as SaveIcon,
  FolderOpen as LoadIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Search as SearchIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Group as GroupIcon,
} from '@mui/icons-material';

// Keyboard shortcut definitions - Avoiding browser conflicts
const SHORTCUTS = {
  // File Operations
  'Ctrl+Shift+S': {
    action: 'save', description: 'Save Flow', icon: <SaveIcon />, category: 'File',
  },
  'Ctrl+Shift+O': {
    action: 'load', description: 'Load Flow', icon: <LoadIcon />, category: 'File',
  },
  'Ctrl+Shift+N': {
    action: 'new', description: 'New Flow', icon: <AddIcon />, category: 'File',
  },

  // Execution
  F5: {
    action: 'execute', description: 'Execute Flow', icon: <PlayIcon />, category: 'Execution',
  },
  'Ctrl+F5': {
    action: 'execute_debug', description: 'Execute with Debug', icon: <PlayIcon />, category: 'Execution',
  },

  // Edit Operations
  'Ctrl+Shift+Z': {
    action: 'undo', description: 'Undo', icon: <UndoIcon />, category: 'Edit',
  },
  'Ctrl+Shift+Y': {
    action: 'redo', description: 'Redo', icon: <RedoIcon />, category: 'Edit',
  },
  'Ctrl+Shift+C': {
    action: 'copy', description: 'Copy Selected', icon: <CopyIcon />, category: 'Edit',
  },
  'Ctrl+Shift+V': {
    action: 'paste', description: 'Paste', icon: <PasteIcon />, category: 'Edit',
  },
  Delete: {
    action: 'delete', description: 'Delete Selected', icon: <DeleteIcon />, category: 'Edit',
  },
  'Ctrl+Shift+A': {
    action: 'select_all', description: 'Select All', icon: <AddIcon />, category: 'Edit',
  },

  // Connection Operations
  'Ctrl+Click': {
    action: 'connect_nodes', description: 'Connect Nodes', icon: <GroupIcon />, category: 'Connection',
  },
  'Cmd+Click': {
    action: 'connect_nodes', description: 'Connect Nodes (Mac)', icon: <GroupIcon />, category: 'Connection',
  },

  // Navigation
  Escape: {
    action: 'escape', description: 'Cancel/Clear Selection', icon: <HelpIcon />, category: 'Navigation',
  },
  Tab: {
    action: 'next_element', description: 'Next Element', icon: <HelpIcon />, category: 'Navigation',
  },
  'Shift+Tab': {
    action: 'prev_element', description: 'Previous Element', icon: <HelpIcon />, category: 'Navigation',
  },

  // View
  'Ctrl+Plus': {
    action: 'zoom_in', description: 'Zoom In', icon: <ZoomInIcon />, category: 'View',
  },
  'Ctrl+Minus': {
    action: 'zoom_out', description: 'Zoom Out', icon: <ZoomOutIcon />, category: 'View',
  },
  'Ctrl+0': {
    action: 'zoom_reset', description: 'Reset Zoom', icon: <ZoomInIcon />, category: 'View',
  },
  F11: {
    action: 'fullscreen', description: 'Toggle Fullscreen', icon: <FullscreenIcon />, category: 'View',
  },

  // Search & Help
  'Ctrl+Shift+F': {
    action: 'search', description: 'Search Nodes', icon: <SearchIcon />, category: 'Search',
  },
  F1: {
    action: 'help', description: 'Show Help', icon: <HelpIcon />, category: 'Help',
  },
  'Ctrl+Shift+?': {
    action: 'shortcuts', description: 'Show Shortcuts', icon: <KeyboardIcon />, category: 'Help',
  },

  // Settings
  'Ctrl+Shift+,': {
    action: 'settings', description: 'Open Settings', icon: <SettingsIcon />, category: 'Settings',
  },

  // Flow Builder Specific
  'Ctrl+E': {
    action: 'export', description: 'Export Flow', icon: <ExportIcon />, category: 'File',
  },
  'Ctrl+I': {
    action: 'import', description: 'Import Flow', icon: <ImportIcon />, category: 'File',
  },
  'Ctrl+R': {
    action: 'run', description: 'Run Flow', icon: <PlayIcon />, category: 'Execution',
  },
  'Ctrl+D': {
    action: 'duplicate', description: 'Duplicate Selected', icon: <CopyIcon />, category: 'Edit',
  },
  'Ctrl+G': {
    action: 'group', description: 'Group Selected', icon: <GroupIcon />, category: 'Edit',
  },
  'Ctrl+U': {
    action: 'ungroup', description: 'Ungroup Selected', icon: <GroupIcon />, category: 'Edit',
  },
};

// Hook for managing keyboard shortcuts
export const useKeyboardShortcuts = (handlers = {}) => {
  const handleKeyDown = useCallback((event) => {
    // Don't handle shortcuts when typing in input fields
    const tag = document.activeElement.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') {
      return;
    }

    // Build the key combination string
    const modifiers = [];
    if (event.ctrlKey || event.metaKey) modifiers.push('Ctrl');
    if (event.shiftKey) modifiers.push('Shift');
    if (event.altKey) modifiers.push('Alt');

    const { key } = event;
    let keyCombo = '';

    if (modifiers.length > 0) {
      keyCombo = `${modifiers.join('+')}+${key}`;
    } else {
      keyCombo = key;
    }

    // Handle special keys
    if (key === '+' || key === '=') {
      keyCombo = `${modifiers.join('+')}+Plus`;
    } else if (key === '-') {
      keyCombo = `${modifiers.join('+')}+Minus`;
    } else if (key === ',') {
      keyCombo = `${modifiers.join('+')}+,`;
    } else if (key === '?') {
      keyCombo = `${modifiers.join('+')}+?`;
    } else if (key === 'Shift') {
      // Handle Shift key combinations
      keyCombo = `${modifiers.join('+')}+Shift`;
    }

    // Find matching shortcut
    const shortcut = SHORTCUTS[keyCombo];
    if (shortcut && handlers[shortcut.action]) {
      event.preventDefault();
      event.stopPropagation();
      handlers[shortcut.action](event);
    }
  }, [handlers]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return { shortcuts: SHORTCUTS };
};

// Component to display keyboard shortcuts help
export function KeyboardShortcutsHelp({ open, onClose }) {
  if (!open) return null;

  const categories = [...new Set(Object.values(SHORTCUTS).map((s) => s.category))];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
      onClick={onClose}
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          p: 3,
          maxWidth: 800,
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 3,
        }}
        >
          <KeyboardIcon color="primary" />
          <Typography variant="h5" component="h2">
            Keyboard Shortcuts
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Press
          {' '}
          <Chip label="Esc" size="small" />
          {' '}
          or click outside to close
        </Typography>

        {categories.map((category) => (
          <Box key={category} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              {category}
            </Typography>
            <Stack spacing={1}>
              {Object.entries(SHORTCUTS)
                .filter(([_, shortcut]) => shortcut.category === category)
                .map(([keyCombo, shortcut]) => (
                  <Box
                    key={keyCombo}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {shortcut.icon}
                      <Typography variant="body2">
                        {shortcut.description}
                      </Typography>
                    </Box>
                    <Chip
                      label={keyCombo}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Box>
                ))}
            </Stack>
            {category !== categories[categories.length - 1] && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default { useKeyboardShortcuts, KeyboardShortcutsHelp, SHORTCUTS };
